import { useCallback, useEffect, useState } from 'react'
import { Receipt, Clock, CheckCircle2, AlertTriangle, HandCoins } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { KpiCard } from '@/components/ui/KpiCard'
import { toast } from '@/components/ui/Toast'
import { brl, dateBR } from '@/lib/format'
import { PartnerService } from '@/services/modules/PartnerService'
import type { EarningsReport, CommissionDetail } from '@/services/modules/RevenuesService'
import { currentMonthRange, ReportFilters, BarList } from '@/pages/Revenues/reportShared'
import { CommissionDetailDrawer } from '@/components/CommissionDetailDrawer'

type CardKey = 'receive-consolidado' | 'receive-pendente' | 'pay-consolidado' | 'pay-pendente'
const CARD_META: Record<CardKey, { title: string; role: 'receive' | 'pay'; status: 'consolidado' | 'pendente'; showRecipient: boolean }> = {
  'receive-consolidado': { title: 'A receber — consolidado', role: 'receive', status: 'consolidado', showRecipient: false },
  'receive-pendente':    { title: 'A receber — pendente',    role: 'receive', status: 'pendente',    showRecipient: false },
  'pay-consolidado':     { title: 'A pagar à rede — consolidado', role: 'pay', status: 'consolidado', showRecipient: true },
  'pay-pendente':        { title: 'A pagar à rede — pendente',    role: 'pay', status: 'pendente',    showRecipient: true },
}

export function PartnerReportEarningsPage() {
  const init = currentMonthRange()
  const [from, setFrom] = useState(init.from)
  const [to, setTo] = useState(init.to)
  const [data, setData] = useState<EarningsReport | null>(null)
  const [loading, setLoading] = useState(false)

  // Drawer de detalhamento por card.
  const [openCard, setOpenCard] = useState<CardKey | null>(null)
  const [detail, setDetail] = useState<CommissionDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const load = useCallback(async (f: string, t: string) => {
    if (!f || !t) return
    setLoading(true)
    try { setData(await PartnerService.reportsEarnings(f, t)) }
    catch { toast.error('Erro ao carregar o relatório de ganhos.') }
    finally { setLoading(false) }
  }, [])
  useEffect(() => { load(init.from, init.to) }, [load, init.from, init.to])

  const openDetail = useCallback(async (key: CardKey) => {
    const m = CARD_META[key]
    setOpenCard(key)
    setDetail(null)
    setDetailLoading(true)
    try { setDetail(await PartnerService.reportsEarningsDetail({ from, to, role: m.role, status: m.status })) }
    catch { toast.error('Erro ao carregar o detalhamento.') }
    finally { setDetailLoading(false) }
  }, [from, to])

  const activeMeta = openCard ? CARD_META[openCard] : null

  return (
    <>
      <PageHeader title="Ganhos" subtitle="Faturamento e recebimentos da sua rede no período"
        crumbs={[{ label: 'Financeiro' }, { label: 'Relatórios' }, { label: 'Ganhos' }]} />
      <ReportFilters from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={() => load(from, to)} loading={loading} />

      {!data ? (
        <div className="text-sm text-gray-500">{loading ? 'Carregando…' : 'Sem dados.'}</div>
      ) : (
        <div className="space-y-6">
          {/* Faturamento da rede (valor das faturas — NÃO é comissão) */}
          <div>
            <h2 className="mb-1 text-sm font-semibold text-gray-700">Faturamento da rede</h2>
            <p className="mb-3 text-xs text-gray-500">Valor das faturas dos associados e empresas da sua rede no período.</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard label="Faturamento total" accent="brand" icon={<Receipt size={18} />} value={brl(data.totals.faturamento)} hint="Todas as faturas do período" />
              <KpiCard label="Pago" accent="green" icon={<CheckCircle2 size={18} />} value={brl(data.totals.pago)} hint="Faturas recebidas" />
              <KpiCard label="Pendente" accent="amber" icon={<Clock size={18} />} value={brl(data.totals.pendente)} hint="Faturas a vencer / não pagas" />
              <KpiCard label="Vencido" accent="rose" icon={<AlertTriangle size={18} />} value={brl(data.totals.vencido)} hint="Faturas em atraso" />
            </div>
          </div>

          {/* Comissões (o que VOCÊ ganha e o que repassa à rede) — clique para detalhar */}
          <div>
            <h2 className="mb-1 text-sm font-semibold text-gray-700">Suas comissões</h2>
            <p className="mb-3 text-xs text-gray-500">Clique em um card para ver, fatura a fatura, o cliente, a base, o percentual aplicado e o valor da comissão.</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard label="A receber — consolidado" accent="green" icon={<HandCoins size={18} />} value={brl(data.totals.commissionReceive ?? 0)} hint="Suas comissões de faturas JÁ pagas" onClick={() => openDetail('receive-consolidado')} />
              <KpiCard label="A receber — pendente" accent="amber" icon={<HandCoins size={18} />} value={brl(data.totals.commissionReceivePending ?? 0)} hint="Suas comissões de faturas NÃO pagas" onClick={() => openDetail('receive-pendente')} />
              <KpiCard label="A pagar à rede — consolidado" accent="violet" icon={<HandCoins size={18} />} value={brl(data.totals.commissionDue)} hint="Repasse de faturas JÁ pagas" onClick={() => openDetail('pay-consolidado')} />
              <KpiCard label="A pagar à rede — pendente" accent="rose" icon={<HandCoins size={18} />} value={brl(data.totals.commissionDuePending ?? 0)} hint="Repasse de faturas NÃO pagas" onClick={() => openDetail('pay-pendente')} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-md bg-white ring-1 ring-gray-200 p-5">
              <h3 className="mb-1 font-medium text-gray-900 text-sm">Recebido por dia</h3>
              <p className="mb-4 text-xs text-gray-500">Valor pago por dia no período</p>
              <BarList empty="Nenhum recebimento no período." data={data.paymentsByDay.map(d => ({ label: dateBR(d.date), value: d.total, valueLabel: brl(d.total) }))} />
            </div>
            <div className="rounded-md bg-white ring-1 ring-gray-200 p-5">
              <h3 className="mb-1 font-medium text-gray-900 text-sm">Forma de pagamento</h3>
              <p className="mb-4 text-xs text-gray-500">Valor recebido por método</p>
              <BarList empty="Nenhum recebimento no período." data={data.byPaymentMethod.map(m => ({ label: m.method, value: m.total, valueLabel: brl(m.total) }))} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-md bg-white ring-1 ring-gray-200 p-5">
              <h3 className="mb-1 font-medium text-gray-900 text-sm">Recebido por associado</h3>
              <p className="mb-4 text-xs text-gray-500">Por tipo (modalidade)</p>
              <BarList empty="Nenhum recebimento de associados." data={data.bySubscriberType.map(t => ({ label: t.type, value: t.total, valueLabel: brl(t.total) }))} />
            </div>
            <div className="rounded-md bg-white ring-1 ring-gray-200 p-5">
              <h3 className="mb-1 font-medium text-gray-900 text-sm">Recebido por empresa</h3>
              <p className="mb-4 text-xs text-gray-500">Por tipo (modalidade)</p>
              <BarList empty="Nenhum recebimento de empresas." data={data.byCompanyType.map(t => ({ label: t.type, value: t.total, valueLabel: brl(t.total) }))} />
            </div>
          </div>
        </div>
      )}

      <CommissionDetailDrawer
        open={openCard !== null}
        onClose={() => setOpenCard(null)}
        title={activeMeta?.title ?? 'Detalhamento'}
        subtitle="Comissões do período, fatura a fatura"
        loading={detailLoading}
        detail={detail}
        showRecipient={activeMeta?.showRecipient}
      />
    </>
  )
}

export default PartnerReportEarningsPage
